import { useMutation } from "@tanstack/react-query";
import { actions, isInputError } from "astro:actions";
import { navigate } from "astro:transitions/client";
import Link from "astro-typesafe-routes/link/react";
import { $path } from "astro-typesafe-routes/path";

import { Checkbox } from "~/components/Checkbox";
import { DialogBody } from "~/components/DialogBody";
import { DialogFooter } from "~/components/DialogFooter";
import { DialogTitle } from "~/components/DialogTitle";
import { RoleSelect } from "~/components/RoleSelect";
import { StrongPasswordField } from "~/components/StrongPasswordField";
import { TextField } from "~/components/TextField";

import { SourceSelect } from "./SourceSelect";

const SIGN_UP_BUTTON_TEXT = "Sign up";

export const SignUpForm: React.FC<{
  children: React.ReactElement;
  formData: FormData;
}> = ({ children, formData }) => {
  const { mutate, error, isPending, isSuccess } = useMutation({
    mutationFn: actions.account.signup.orThrow,
    onSuccess: (email) => {
      navigate(
        $path({
          to: "/signup/success",
          searchParams: {
            email,
          },
        }),
      );
    },
  });

  const inputError = isInputError(error) ? error : null;
  return (
    <form
      autoComplete="off"
      className="contents"
      onSubmit={(event) => {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);
        mutate(formData);
      }}
    >
      {children}
      <DialogBody className="space-y-4">
        <DialogTitle>Create your account</DialogTitle>
        <TextField
          required
          autoFocus
          name="email"
          type="email"
          label="Email"
          defaultValue={formData.get("email")?.toString()}
          error={inputError?.fields.email?.[0]}
        />
        <TextField
          label="Name"
          name="name"
          placeholder="What do you want to be called? (optional)"
        />
        <StrongPasswordField error={inputError?.fields.password?.[0]} />
        <RoleSelect />
        <SourceSelect />
        <Checkbox name="newsletter">
          I want to recieve news and updates from Knoword
        </Checkbox>
        <p className="text-sm">
          By clicking &quot;{SIGN_UP_BUTTON_TEXT}&quot;, you agree to
          Knoword&apos;s{" "}
          <Link className="link" to="/terms">
            Terms of Service
          </Link>{" "}
          and{" "}
          <Link className="link" to="/privacy">
            Privacy Policy
          </Link>
          .
        </p>
      </DialogBody>
      <DialogFooter>
        <button
          disabled={isPending || isSuccess}
          type="submit"
          className="btn btn-secondary btn-block"
        >
          {SIGN_UP_BUTTON_TEXT}
        </button>
      </DialogFooter>
    </form>
  );
};
